
import { AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from '@/components/ui/card';

interface LoadingIndicatorProps {
  progress: number;
  stage: string;
  error?: string;
}

// 단계별 정보를 정의
const STAGE_INFO = [
  {
    threshold: 0,
    text: "서버에서 데이터 요청 중",
    description: "인스타그램 계정의 기본 정보를 확인하고 있습니다"
  },
  {
    threshold: 30,
    text: "데이터 처리 중",
    description: "게시물, 팔로워 및 프로필 세부정보를 분석 중입니다"
  },
  {
    threshold: 70,
    text: "저장된 계정 정보 확인 중",
    description: "AI 대화를 위해 데이터를 최적화하는 중입니다"
  },
  {
    threshold: 95,
    text: "거의 완료됨",
    description: "잠시만 기다려 주세요"
  }
];

export function LoadingIndicator({
  progress,
  error
}: LoadingIndicatorProps) {
  const hasError = !!error;

  // 스테이지별 진행 상태 계산
  const stageCompletion = Math.min(100, Math.round((progress / 100) * 3));

  // 현재 활성화된 단계 결정
  const getCurrentStageInfo = () => {
    for (let i = STAGE_INFO.length - 1; i >= 0; i--) {
      if (progress >= STAGE_INFO[i].threshold) {
        return STAGE_INFO[i];
      }
    }
    return STAGE_INFO[0];
  };

  const currentStageInfo = getCurrentStageInfo();

  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center px-4 overflow-hidden bg-background/95 z-50">
      {hasError ? (
        // 에러 상태
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
          className="flex flex-col items-center text-center"
        >
          <div className="relative mb-6">
            <div className="absolute inset-0 bg-destructive/20 blur-2xl rounded-full"></div>
            <div className="relative bg-destructive/10 w-20 h-20 rounded-full flex items-center justify-center">
              <motion.div
                initial={{ rotate: 0 }}
                animate={{ rotate: 10, scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
              >
                <AlertCircle className="h-10 w-10 text-destructive" />
              </motion.div>
            </div>
          </div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="space-y-3 max-w-md"
          >
            <h3 className="text-xl font-bold text-destructive">요청 처리 실패</h3>
            <p className="text-muted-foreground text-sm">{error}</p>

            <motion.div
              className="mt-4 text-sm text-muted-foreground border border-muted rounded-xl p-3 bg-muted/30"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <p>잠시 후 다시 시도하시거나 다른 계정 ID를 검색해 보세요.</p>
            </motion.div>
          </motion.div>
        </motion.div>
      ) : (
        // 로딩 상태 - 완전히 새로운 디자인
        <motion.div
          className="w-full max-w-md flex flex-col items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {/* 로딩 헤더 */}
          <div className="relative w-full flex justify-center mb-6">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-purple-400/30 to-accent/20 blur-3xl opacity-70 rounded-full"></div>
            <motion.div
              className="relative z-10 bg-gradient-to-br from-primary/10 to-accent/10 backdrop-blur-md w-24 h-24 sm:w-28 sm:h-28 rounded-full border border-white/10 flex items-center justify-center"
              animate={{
                boxShadow: ["0 0 0 rgba(139, 92, 246, 0.3)", "0 0 30px rgba(139, 92, 246, 0.5)", "0 0 0 rgba(139, 92, 246, 0.3)"],
              }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              <motion.div
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center"
                animate={{
                  scale: [1, 1.05, 1],
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{
                    duration: 8,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                  className="text-white"
                >
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C8.13 2 5 5.13 5 9C5 10.57 5.46 12.03 6.24 13.26L12 21.5L17.76 13.26C18.54 12.03 19 10.57 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" fill="currentColor" />
                  </svg>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>

          {/* 로딩 텍스트 및 백분율 */}
          <motion.div
            className="text-center space-y-1 mb-5 h-20"
            animate={{ opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            <motion.h3
              className="text-xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent"
              key={currentStageInfo.text}
              initial={{ y: -10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 10, opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <AnimatePresence mode="wait">
                <motion.span
                  key={currentStageInfo.text}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  {currentStageInfo.text}
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0, 1, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    ...
                  </motion.span>
                </motion.span>
              </AnimatePresence>
            </motion.h3>
            <AnimatePresence mode="wait">
              <motion.p
                className="text-muted-foreground text-sm h-12 flex items-center justify-center"
                key={currentStageInfo.description}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                {currentStageInfo.description}
              </motion.p>
            </AnimatePresence>
          </motion.div>

          {/* 새로운 진행률 표시 */}
          <div className="w-full mb-5">
            <div className="relative h-2 w-full bg-muted/50 rounded-full overflow-hidden mb-2">
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-primary via-purple-500 to-accent"
                initial={{ width: '0%' }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />

              {/* 반짝이는 효과 */}
              <motion.div
                className="absolute top-0 bottom-0 w-16 bg-white/30 -translate-x-20"
                style={{ transform: "skewX(30deg) translateX(-100%)" }}
                animate={{ translateX: ['0%', '100%'] }}
                transition={{ duration: 1.5, ease: "easeInOut", repeat: Infinity, repeatDelay: 1 }}
              />
            </div>

            <div className="flex justify-between items-center text-xs">
              <span className="text-muted-foreground">{stageCompletion}/3 단계</span>
              <span className="font-medium">{progress}%</span>
            </div>
          </div>

          {/* 스테이지 진행 표시 카드 */}
          <div className="w-full">
            <Card className="overflow-hidden backdrop-blur-sm bg-background/80 border shadow-md">
              <div className="p-3 overflow-hidden">
                <h4 className="text-xs font-medium mb-2 text-muted-foreground uppercase tracking-wider">처리 과정</h4>
                <div className="space-y-2.5">
                  <StageItem
                    isActive={progress >= 0 && progress < 30}
                    isComplete={progress >= 30}
                    label="계정 정보 분석"
                    description={STAGE_INFO[0].description}
                  />

                  <StageItem
                    isActive={progress >= 30 && progress < 70}
                    isComplete={progress >= 70}
                    label="프로필 데이터 처리"
                    description={STAGE_INFO[1].description}
                  />

                  <StageItem
                    isActive={progress >= 70 && progress < 95}
                    isComplete={progress >= 95}
                    label="데이터 최적화"
                    description={STAGE_INFO[2].description}
                  />
                </div>
              </div>
            </Card>
          </div>
        </motion.div>
      )}
    </div>
  );
}

// 단계별 상태 표시 컴포넌트
interface StageItemProps {
  isActive: boolean;
  isComplete: boolean;
  label: string;
  description: string;
}

function StageItem({ isActive, isComplete, label, description }: StageItemProps) {
  return (
    <div className="flex items-start gap-2">
      <div className={`relative mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${isComplete
        ? "border-green-500 bg-green-500/20"
        : isActive
          ? "border-primary bg-primary/20 animate-pulse"
          : "border-muted-foreground/30 bg-muted"
        }`}>
        {isComplete && (
          <motion.svg
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="h-3 w-3 text-green-500"
            xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"
          >
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </motion.svg>
        )}
        {isActive && !isComplete && (
          <motion.div
            className="h-1.5 w-1.5 rounded-full bg-primary"
            animate={{ scale: [1, 1.5, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
        )}
      </div>

      <div className="space-y-0.5 flex-1 overflow-hidden">
        <p className={`text-xs font-medium truncate ${isComplete ? "text-green-500" : isActive ? "text-primary" : "text-muted-foreground/70"
          }`}>
          {label}
        </p>
        <AnimatePresence>
          {isActive && (
            <motion.p
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="text-xs text-muted-foreground line-clamp-2"
            >
              {description}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}